import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckIcon,
  Loader2Icon,
  PencilIcon,
  ScanLineIcon,
  WifiOffIcon,
  XIcon,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DotStatus,
  HubMonitoring,
  MonitoringSite,
  MonitoringSlot,
  ProjectMonitoring,
  SampleMonitoring,
  useMonitoring,
  useSetInternalTeamOnlyLabel,
  useSetScannerResolution,
} from '@/services/field.service';
import { BASE } from '../../basePath';
import BackofficePage from '../../components/BackofficePage';
import MonitoringTabs from './MonitoringTabs';
import HubCard from './HubCard';
import {
  Dot,
  DotRow,
  DotStrip,
  RAIL_COLOR,
  RAIL_WIDTH_PX,
  RAIL_X_PX,
  ScrollSync,
  TICK_PX,
  useScrollSync,
  useWheelPan,
} from './FieldStrip';
import { utcStamp } from './fieldTime';
import { saveErrorMessage } from './saveError';

// The three raw files checked per acquisition, top to bottom.
const FILE_ROWS: DotRow<MonitoringSlot>[] = (
  ['json', 'jpg', 'jp2'] as const
).map((kind) => ({
  label: kind,
  dot: (slot) => ({ color: slot[kind], title: dotTitle(slot, kind) }),
}));

/**
 * One monitoring page. The field devices and the bench ones are the same grid
 * over a different set of samples — same rules, same layout, kept apart because
 * they are read for different reasons: a gap on a bench device is a test
 * running its course, not an incident.
 */
export default function BackOfficeMonitoring({ site }: { site: MonitoringSite }) {
  const { data, isLoading, error } = useMonitoring(site);

  if (isLoading)
    return (
      <Frame site={site}>
        <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
      </Frame>
    );
  if (error)
    return (
      <Frame site={site}>
        <div className="text-red-400 text-sm">{error.toString()}</div>
      </Frame>
    );

  const projects = data ?? [];

  return (
    <Frame site={site}>
      <Legend />
      {projects.length === 0 && (
        <div className="text-sm text-[#6b7480] mb-6">
          No samples are monitored {site === 'BENCH' ? 'on the bench' : 'in the field'}{' '}
          yet. Pick some in{' '}
          <Link
            to={`${BASE}/backOffice/monitoring/settings`}
            className="text-[#74c69d] hover:underline"
          >
            Settings
          </Link>
          .
        </div>
      )}
      <div className="space-y-10">
        {projects.map((project) => (
          <ProjectGroup key={project.projectId} project={project} />
        ))}
      </div>
    </Frame>
  );
}

/** One project: its hubs with their samples, then the hub-less samples. */
function ProjectGroup({ project }: { project: ProjectMonitoring }) {
  return (
    <div>
      {/* Rides along as you scroll through the project's hubs, so you always
          know which project you are looking at, and is pushed off by the next
          project's own heading. The cards are exactly this wide, so the solid
          background covers everything passing underneath. */}
      <h2 className="sticky top-0 z-10 bg-[#0f141a] border-b border-[#1e242a] py-2 mb-3 text-sm font-semibold text-white flex items-center gap-2">
        {project.projectName}
        <InternalTeamOnlyLabel project={project} />
      </h2>
      <div className="space-y-6">
        {project.hubs.map((hub) => (
          <HubGroup key={hub.macAddress} hub={hub} />
        ))}

        {project.orphanedSamples.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <WifiOffIcon className="h-4 w-4 text-[#6b7480]" />
              <h3 className="text-xs uppercase tracking-wider text-[#6b7480] font-medium">
                Samples linked to no hub
              </h3>
            </div>
            <div className="space-y-3">
              {project.orphanedSamples.map((sample) => (
                <SampleStrip key={sample.sampleId} sample={sample} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Our internal shorthand for a project, sitting after its real name. Backoffice
 * only: the project's members neither know nor would recognise this label, so
 * it must never appear in a user-facing view.
 */
function InternalTeamOnlyLabel({ project }: { project: ProjectMonitoring }) {
  const current = project.internalTeamOnlyLabel ?? '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(current);
  const save = useSetInternalTeamOnlyLabel();

  const commit = () => {
    setEditing(false);
    if (draft.trim() !== current) {
      save.mutate({ projectId: project.projectId, label: draft });
    }
  };

  if (editing) {
    return (
      <span className="flex items-center gap-1 font-normal">
        <input
          autoFocus
          value={draft}
          maxLength={60}
          placeholder="Internal label"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            else if (event.key === 'Escape') setEditing(false);
          }}
          className="w-48 px-2 py-0.5 rounded text-xs bg-[#0f141a] border border-[#2d2d2d] text-[#d8dde2] placeholder:text-[#6b7480] focus:outline-none focus:border-[#74c69d]"
        />
        <IconButton title="Save" onClick={commit}>
          <CheckIcon className="h-3 w-3" />
        </IconButton>
        <IconButton title="Cancel" onClick={() => setEditing(false)}>
          <XIcon className="h-3 w-3" />
        </IconButton>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 font-normal">
      {current && <span className="text-[#a8b0bb]">{current}</span>}
      <IconButton
        title="Edit our internal label for this project (team only — never shown to its users)"
        onClick={() => {
          setDraft(current);
          setEditing(true);
        }}
      >
        <PencilIcon className="h-3 w-3" />
      </IconButton>
    </span>
  );
}

function IconButton({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="h-5 w-5 flex items-center justify-center rounded text-[#6b7480] hover:text-white hover:bg-[#1a2126]"
    >
      {children}
    </button>
  );
}

/**
 * One hub: its card at the group's full width, then the samples wired to it,
 * inset and hanging off a rail that starts at the hub card's bottom edge:
 *
 *     Hub card
 *        |
 *        |- Sample card
 *        |
 *        |- Sample card
 *
 * The size difference is the hierarchy — no extra colour, which this page keeps
 * for the dots. The hub card pays for its extra width out of its own dot-strip
 * gutter, so every card's dots still line up (see SAMPLE_INDENT_PX).
 */
function HubGroup({ hub }: { hub: HubMonitoring }) {
  const sync = useScrollSync();

  return (
    <div>
      <HubCard hub={hub} sync={sync} />
      <ScheduleHistory hub={hub} />

      {/* The rail's own top edge is the hub card's bottom edge, so the line
          visibly comes out of the hub rather than floating below it. */}
      <div
        className="space-y-3 pt-3 pb-1"
        style={{
          marginLeft: RAIL_X_PX,
          paddingLeft: TICK_PX,
          borderLeft: `${RAIL_WIDTH_PX}px solid ${RAIL_COLOR}`,
        }}
      >
        {hub.samples.length > 0 ? (
          hub.samples.map((sample) => (
            <div key={sample.sampleId} className="relative">
              <span
                className="absolute top-1/2 h-px"
                style={{
                  left: -TICK_PX,
                  width: TICK_PX,
                  backgroundColor: RAIL_COLOR,
                }}
              />
              <SampleStrip sample={sample} sync={sync} />
            </div>
          ))
        ) : (
          <div className="text-xs text-[#6b7480] italic py-2">
            No monitored samples on this hub
          </div>
        )}
      </div>
    </div>
  );
}

function Frame({
  site,
  children,
}: {
  site: MonitoringSite;
  children: React.ReactNode;
}) {
  return (
    // Wide: every pixel of margin is a day of history the grids can show.
    // The heading names the section and the tabs name the page, so the title
    // stays put as you move between Field, Bench and Settings.
    <BackofficePage
      title="Monitoring"
      description={
        site === 'BENCH'
          ? 'Devices kept at the office, running for development or pre-production testing.'
          : 'Devices planted out on real sites.'
      }
      wide
    >
      <MonitoringTabs />
      {children}
    </BackofficePage>
  );
}

/**
 * The schedules a hub ran on, oldest first — why its dots change spacing part
 * way along, and what each stretch was judged against. Absent on a hub that was
 * never re-scheduled, which is the normal case: one schedule is already on the
 * hub card, and repeating it here would say nothing.
 */
function ScheduleHistory({ hub }: { hub: HubMonitoring }) {
  const segments = hub.scheduleSegments ?? [];
  if (segments.length < 2) return null;

  return (
    <div
      className="pt-2 text-[11px] text-[#6b7480] flex flex-wrap items-center gap-x-2 gap-y-1"
      style={{ marginLeft: RAIL_X_PX }}
    >
      <span className="uppercase tracking-wider">Schedule</span>
      {segments.map((segment, index) => (
        <span key={index} className="flex items-center gap-2">
          {index > 0 && <span className="text-[#3a4249]">→</span>}
          <span
            className={
              index === segments.length - 1 ? 'text-[#a8b0bb]' : undefined
            }
            title={segment.cron}
          >
            {segment.label ?? `unsupported (${segment.cron})`}
            {segment.fromTime && (
              <span className="text-[#4f5862]">
                {' '}
                from {utcStamp(segment.fromTime, true)}
              </span>
            )}
          </span>
        </span>
      ))}
    </div>
  );
}

function Legend() {
  const items: { color: DotStatus; label: string }[] = [
    { color: 'green', label: 'file present / hub status reported' },
    { color: 'yellow', label: 'present, empty (0 B)' },
    { color: 'red', label: 'missing' },
    { color: 'blue', label: 'off-schedule: config wake-up, or extra file' },
    { color: 'purple', label: 'off-schedule wake-up in normal mode' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6 text-xs text-[#a8b0bb]">
      {items.map((it) => (
        <span key={it.color} className="flex items-center gap-1.5">
          <Dot color={it.color} />
          {it.label}
        </span>
      ))}
      {/* The schedules, the S3 timestamps and the day columns are all UTC, so
          the page never shows anything else — said once, here. */}
      <span className="text-[#6b7480]">all times UTC</span>
    </div>
  );
}

/**
 * One monitored sample: schedule label + the chronological strip of dots.
 * Joins `sync`'s scroll group when given one; a sample hanging off no hub is
 * linked to nothing and pans on its own.
 */
function SampleStrip({
  sample,
  sync,
}: {
  sample: SampleMonitoring;
  sync?: ScrollSync;
}) {
  const ownSync = useScrollSync();
  const scroll = sync ?? ownSync;
  const cardRef = useWheelPan<HTMLDivElement>(scroll);

  return (
    // A header shaped like the hub's, but the card itself stays plain: the
    // inset and the rail carry the hierarchy, colour is left to the dots.
    <div
      ref={cardRef}
      className="bg-[#161c22] border border-[#1e242a] rounded-lg p-4"
    >
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-[#1a2126] flex items-center justify-center flex-shrink-0">
            <ScanLineIcon className="h-4 w-4 text-[#8f9aa6]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#6b7480] flex items-center gap-1">
              <span>
                Sample
                {sample.scannerSerialNumber && (
                  <>
                    {' · scanner '}
                    <span className="font-mono normal-case">
                      {sample.scannerSerialNumber}
                    </span>
                  </>
                )}
              </span>
              {sample.scannerSerialNumber && (
                <ScannerResolution
                  serialNumber={sample.scannerSerialNumber}
                  resolutionDpi={sample.scannerResolutionDpi}
                />
              )}
            </div>
            {/* The description on hover only: the card has no room for it. */}
            <div
              className="text-sm font-semibold text-white truncate"
              title={sample.sampleDescription ?? undefined}
            >
              {sample.sampleName}
            </div>
          </div>
        </div>
        {/* The schedule and the count are the hub's, said once on its card;
            here only a reason this sample has no strip is worth the room. */}
        {sample.slots.length === 0 && (
          <span className="text-[11px] text-[#6b7480] flex-shrink-0">
            {sample.note ?? 'No schedule'}
          </span>
        )}
      </div>
      {sample.slots.length > 0 && (
        <DotStrip sync={scroll} slots={sample.slots} rows={FILE_ROWS} />
      )}
    </div>
  );
}

/**
 * The scanner's acquisition resolution, next to its serial number. It is a
 * device setting rather than a project one — it is pushed to the scanner's
 * config on the hub — so the backoffice is the only place it is shown at all,
 * and this is the only place it can be changed.
 */
const DPI_CHOICES = [300, 600, 1200];

function ScannerResolution({
  serialNumber,
  resolutionDpi,
}: {
  serialNumber: string;
  resolutionDpi: number | null;
}) {
  const [editing, setEditing] = useState(false);
  // Holds the just-saved value until the monitoring query catches up: it
  // re-reads every hub's S3 history, which is a long moment to sit showing the
  // resolution the scanner is no longer on.
  const [saved, setSaved] = useState<number | null>(null);
  const save = useSetScannerResolution();
  const current = saved ?? resolutionDpi;

  useEffect(() => {
    if (saved !== null && resolutionDpi === saved) setSaved(null);
  }, [resolutionDpi, saved]);

  const commit = (value: number) => {
    setEditing(false);
    if (value === current) return;
    save.mutate(
      { serialNumber, resolutionDpi: value },
      { onSuccess: () => setSaved(value) },
    );
  };

  if (editing) {
    return (
      <select
        autoFocus
        value={current ?? ''}
        onChange={(event) => commit(Number(event.target.value))}
        onBlur={() => setEditing(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setEditing(false);
        }}
        className="normal-case px-1 py-0.5 rounded text-[11px] bg-[#0f141a] border border-[#2d2d2d] text-[#d8dde2] focus:outline-none focus:border-[#74c69d]"
      >
        {/* A scanner sitting on something other than the three offered — read
            back from its own config — must still show what it is on. */}
        {current != null && !DPI_CHOICES.includes(current) && (
          <option value={current}>{current} DPI</option>
        )}
        {DPI_CHOICES.map((dpi) => (
          <option key={dpi} value={dpi}>
            {dpi} DPI
          </option>
        ))}
      </select>
    );
  }

  return (
    <span className="flex items-center gap-1">
      <span className="normal-case text-[#a8b0bb]">
        {'· '}
        {current != null ? `${current} DPI` : '— DPI'}
      </span>
      {save.isPending ? (
        <Loader2Icon className="h-3 w-3 animate-spin text-[#6b7480]" />
      ) : (
        <IconButton
          title="Change the scanner's acquisition resolution — written to its config on the hub"
          onClick={() => setEditing(true)}
        >
          <PencilIcon className="h-3 w-3" />
        </IconButton>
      )}
      {save.isError && (
        <span
          className="normal-case text-[#d9534f]"
          title={saveErrorMessage(save.error)}
        >
          save failed
        </span>
      )}
    </span>
  );
}

// ---- helpers -------------------------------------------------------------

function dotTitle(slot: MonitoringSlot, kind: 'json' | 'jpg' | 'jp2'): string {
  if (!slot.scheduled) {
    if (slot[kind] === 'none' || !slot.matchedAt) return '';
    const state = slot[kind] === 'yellow' ? 'empty (0 bytes)' : 'present';
    return `${kind} · unplanned acquisition ${utcStamp(slot.matchedAt, true)} · ${state}`;
  }
  const status = slot[kind];
  const state =
    status === 'green'
      ? 'present'
      : status === 'yellow'
        ? 'empty (0 bytes)'
        : slot.matchedAt
          ? 'missing'
          : 'no acquisition';
  return `${kind} · scheduled ${utcStamp(slot.time)} · ${state}`;
}
